import { motion } from "framer-motion";
import type { FunnelSection } from "../../funnel/flowData";

type TopBarProps = {
  canGoBack: boolean;
  onBack: () => void;
  sections: FunnelSection[];
  sectionProgress: number[];
  isWelcomeStep?: boolean;
  progressVariant?: "sections" | "journeyPills";
  journeyPillIndex?: number;
  useWelcomeChrome?: boolean;
};

export const TopBar = ({
  canGoBack,
  onBack,
  sections,
  sectionProgress,
  isWelcomeStep = false,
  progressVariant = "sections",
  journeyPillIndex = 0,
  useWelcomeChrome = false
}: TopBarProps) => {
  const renderJourneyPillProgress = () => {
    const pillCount = 4;
    const activePillIndex = Math.min(Math.max(journeyPillIndex, 0), pillCount - 1);

    return (
      <div className="journey-pill-progress" aria-label="Journey progress">
        {Array.from({ length: pillCount }, (_, index) => (
          <span
            key={index}
            className={`journey-pill-progress-item ${index === activePillIndex ? "journey-pill-progress-item-active" : ""}`}
            aria-current={index === activePillIndex ? "step" : undefined}
          />
        ))}
      </div>
    );
  };

  const renderProgressSections = () => (
    <div className="progress-sections">
      {sections.map((section, index) => {
        const baseProgress = sectionProgress[index] ?? 0;
        const progress = isWelcomeStep ? (index === 0 ? 1 : 0) : baseProgress;
        const isActive = progress > 0 && progress < 1;
        const isDone = progress >= 1;

        return (
          <div key={section.label} className="progress-section">
            <div
              className={`progress-section-bar ${isActive ? "progress-section-bar-active" : ""} ${isDone ? "progress-section-bar-done" : ""}`}
            >
              <motion.span
                className="progress-section-fill"
                initial={false}
                animate={{ width: `${progress * 100}%` }}
                transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            {!useWelcomeChrome && progress > 0 ? (
              <p className="progress-section-label">{section.label}</p>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  if (!isWelcomeStep) {
    return (
      <header className={`top-bar ${useWelcomeChrome ? "top-bar-welcome" : ""} top-bar-inline-progress`}>
        <button
          type="button"
          className="back-button back-button-inline"
          onClick={onBack}
          disabled={!canGoBack}
          aria-label="Go back"
        >
          <svg
            className="back-button-icon"
            width="11"
            height="18"
            viewBox="0 0 11 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M8.5 2L2.5 9L8.5 16"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {progressVariant === "journeyPills" ? renderJourneyPillProgress() : renderProgressSections()}
      </header>
    );
  }

  return (
    <header
      className={`top-bar ${useWelcomeChrome ? "top-bar-welcome" : ""} ${isWelcomeStep ? "top-bar-current-step-welcome" : ""}`}
    >
      <div className="top-bar-row">
        {canGoBack ? (
          <button
            type="button"
            className="back-button"
            onClick={onBack}
            aria-label="Go back"
          >
            ← Back
          </button>
        ) : (
          <div className="top-bar-spacer" />
        )}
        <p className={`brand-name ${useWelcomeChrome ? "brand-name-welcome" : ""}`}>
          {useWelcomeChrome ? "PARABLES" : "Parables"}
        </p>
        <div className="top-bar-spacer" />
      </div>
      {renderProgressSections()}
    </header>
  );
};
